import { Event } from '@akin-home/events';
import {
  createEventMutation,
  deleteEventMutation,
  eventDataToModel,
  eventModelToCreateGrapqhl,
  eventModelToUpdateData,
  getEventQuery,
  listAllEventsByHabitatQuery,
  updateEventMutation,
} from '@akin-home/events/frontend';
import {
  createCreateMutationHook,
  createOptimisticUpdateMutationHook,
  createQueryKeySet,
} from '@akin-home/utils/react-query';

import { useCallback } from 'react';
import {
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

export * from './useGetEvent';

export const eventQueryKeys = createQueryKeySet({ key: 'events' });

export const useListEventsByHabitatQuery = (
  params: {
    habitatID: string;
    filter?: { from?: string; to?: string };
  },
  options?: UseQueryOptions<Event[], unknown, Event[], any>
) =>
  useQuery(
    eventQueryKeys.list(params),
    async () => {
      const raw_events = await listAllEventsByHabitatQuery(
        params.habitatID,
        params.filter
      );

      return raw_events.map(eventDataToModel) as Event[];
    },
    {
      enabled: !!params?.habitatID,
      ...options,
    }
  );

export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteEventMutation, {
    onSuccess() {
      // Since we are dealing with recurring items etc, just invalidate the queries
      queryClient.invalidateQueries<Event[]>(eventQueryKeys.all);
    },
  });
};

/** Handles update event mutation */
export const useUpdateEvent = createOptimisticUpdateMutationHook({
  keySet: eventQueryKeys,
  mutationFunc: updateEventMutation,
  keyExtractor: (vars) => vars.input?.id,
  updater: ({ variables, item }) => {
    return {
      ...item,
      ...variables.input,
      start: variables.input?.start
        ? new Date(variables.input.start)
        : item.start,
      end: variables.input?.end ? new Date(variables.input.end) : item.end,
    };
  },
});

export const useCreateEvent = createCreateMutationHook({
  keySet: eventQueryKeys,
  mutationFunc: createEventMutation,
});

export const useUpdateEventFromModel = () => {
  const update = useUpdateEvent();

  return useCallback(
    async (event: Event) =>
      update.mutateAsync({ input: await eventModelToUpdateData(event) }),
    [update]
  );
};

export const useCreateEventFromModel = () => {
  const createEvent = useCreateEvent();

  return useCallback(
    async (event: Event) => {
      const input = await eventModelToCreateGrapqhl(event);
      return createEvent.mutateAsync({ input });
    },
    [createEvent]
  );
};

export const useGetEventQuery = (
  id: string | undefined,
  options?: UseQueryOptions
) =>
  useQuery(
    eventQueryKeys.detail(id),
    async () => eventDataToModel(await getEventQuery(id as string)),
    {
      enabled: !!id,
      ...options,
    } as any
  );
